export const SITE_URL = (import.meta.env.VITE_SITE_URL || "https://vibegenie.app").replace(/\/+$/, "");
export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "G-XXXXXXXXXX";
export const GA_DUMMY_MEASUREMENT_ID = "G-XXXXXXXXXX";

export type SeoMeta = {
  title: string;
  description: string;
  canonicalPath: string;
  robots?: string;
};

const defaultDescription = "VibeGenieは、質問・スワイプ・AIで今日作る個人開発アイデアとCodex向け開発プロンプトを見つけるWebアプリです。";

const indexedRobots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
const privateRobots = "noindex,nofollow";

const routeSeo: Array<{ pattern: RegExp; meta: SeoMeta }> = [
  {
    pattern: /^\/$/,
    meta: {
      title: "VibeGenie | 個人開発アイデアとAIプロンプトを見つける",
      description: defaultDescription,
      canonicalPath: "/"
    }
  },
  {
    pattern: /^\/login$/,
    meta: {
      title: "VibeGenie | 個人開発アイデアとAIプロンプトを見つける",
      description: defaultDescription,
      canonicalPath: "/login"
    }
  },
  {
    pattern: /^\/home$/,
    meta: {
      title: "VibeGenie ホーム | 今日作る個人開発アイデアを探す",
      description: "プロフィールと好みから、作り始めやすい個人開発アイデアとAIプロンプトへの導線を提示します。",
      canonicalPath: "/home"
    }
  },
  {
    pattern: /^\/questions$/,
    meta: {
      title: "質問で探す | VibeGenie",
      description: "作りたい形、利用者、機能要件に答えて、個人開発アイデア候補を絞り込みます。",
      canonicalPath: "/questions"
    }
  },
  {
    pattern: /^\/swipe$/,
    meta: {
      title: "スワイプで探す | VibeGenie",
      description: "条件カードをスワイプしながら、好みに合う個人開発アイデアを直感的に見つけます。",
      canonicalPath: "/swipe"
    }
  },
  {
    pattern: /^\/diagnosis$/,
    meta: {
      title: "VibeDoctor 診断 | VibeGenie",
      description: "既存アプリのZIPやメモから、改善ポイントと次に実装するためのAIプロンプトを整理します。",
      canonicalPath: "/diagnosis"
    }
  },
  {
    pattern: /^\/vibe-deploy$/,
    meta: {
      title: "VibeDeploy | VibeGenie",
      description: "アプリのZIPを解析し、CloudflareやVercelなどへデプロイ可能にするための修正プロンプトを生成します。",
      canonicalPath: "/vibe-deploy"
    }
  },
  {
    pattern: /^\/glossary$/,
    meta: {
      title: "用語集 | VibeGenie",
      description: "個人開発、AIプロンプト、バイブコーディングで使う用語を確認できます。",
      canonicalPath: "/glossary"
    }
  },
  {
    pattern: /^\/cloudflare$/,
    meta: {
      title: "Cloudflareセットアップ | VibeGenie",
      description: "VibeCoder向けに、Cloudflareアカウント作成、主要サービス、D1、Workers AI、デプロイ手順を整理したセットアップマニュアルです。",
      canonicalPath: "/cloudflare"
    }
  },
  {
    pattern: /^\/vercel$/,
    meta: {
      title: "Vercelセットアップ | VibeGenie",
      description: "VibeCoder向けに、VercelのGit連携、Preview Deployment、環境変数、独自ドメイン、デプロイ手順を整理したセットアップマニュアルです。",
      canonicalPath: "/vercel"
    }
  },
  {
    pattern: /^\/privacy-policy$/,
    meta: {
      title: "プライバシーポリシー | VibeGenie",
      description: "VibeGenieの取得情報、利用目的、第三者提供、お問い合わせ先について説明します。",
      canonicalPath: "/privacy-policy"
    }
  },
  {
    pattern: /^\/company$/,
    meta: {
      title: "運営会社 | VibeGenie",
      description: "VibeGenieの運営会社情報とお問い合わせ先を掲載しています。",
      canonicalPath: "/company"
    }
  },
  {
    pattern: /^\/(admin|settings|saved|result|ideas|onboarding|diagnosis\/.+|swipe\/result)/,
    meta: {
      title: "VibeGenie",
      description: defaultDescription,
      canonicalPath: "/",
      robots: privateRobots
    }
  }
];

export function getSeoMeta(pathname: string): SeoMeta {
  return routeSeo.find(({ pattern }) => pattern.test(pathname))?.meta ?? {
    title: "VibeGenie | 個人開発アイデアとAIプロンプトを見つける",
    description: defaultDescription,
    canonicalPath: pathname || "/"
  };
}

export function absoluteUrl(path: string) {
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function createJsonLd(meta: SeoMeta) {
  const canonicalUrl = absoluteUrl(meta.canonicalPath);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "パブロフ株式会社",
        url: "https://www.pavlov-inc.co.jp/"
      },
      {
        "@type": "WebApplication",
        "@id": `${SITE_URL}/#webapp`,
        name: "VibeGenie",
        url: `${SITE_URL}/`,
        applicationCategory: "DeveloperApplication",
        operatingSystem: "Web",
        inLanguage: "ja",
        description: defaultDescription,
        creator: {
          "@id": `${SITE_URL}/#organization`
        },
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "JPY"
        }
      },
      {
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: meta.title,
        description: meta.description,
        isPartOf: {
          "@id": `${SITE_URL}/#webapp`
        },
        inLanguage: "ja"
      }
    ]
  };
}

export const defaultRobots = indexedRobots;
